import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fiscalYearLabel } from "@/features/purchase/constants";
import type { ProcessPlanFormValues } from "@/features/planning/schema";
import type {
  ProcessPlan,
  ProcessPlanInput,
  ProcessPlanListRow,
  ProcessPlanOutput,
  ProcessPlanStage,
  ProcessPlanStatus,
} from "@/features/planning/types";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function numOrNull(value: unknown) {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isNaN(n) ? null : n;
}

async function nextPlanNumber(planDate: string): Promise<string> {
  const { data: company } = await supabase
    .from("company")
    .select("plan_prefix, fiscal_year_start_month")
    .limit(1)
    .maybeSingle();
  const prefix =
    ((company?.plan_prefix as string | undefined) ?? "PLAN").trim().toUpperCase() || "PLAN";
  const fy = fiscalYearLabel(planDate, Number(company?.fiscal_year_start_month) || 4);
  const pattern = `${prefix}-${fy}-`;
  const { data, error } = await supabase
    .from("process_plans")
    .select("plan_no")
    .ilike("plan_no", `${pattern}%`)
    .order("plan_no", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.plan_no as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  return `${pattern}${String((Number.isNaN(n) ? 0 : n) + 1).padStart(4, "0")}`;
}

function hydrateInput(raw: ProcessPlanInput): ProcessPlanInput {
  return {
    ...raw,
    planned_qty: money(raw.planned_qty),
    planned_weight: money(raw.planned_weight),
  };
}

function hydrateOutput(raw: ProcessPlanOutput): ProcessPlanOutput {
  return {
    ...raw,
    planned_qty: money(raw.planned_qty),
    planned_weight: money(raw.planned_weight),
    thickness_mm: numOrNull(raw.thickness_mm),
    width_mm: numOrNull(raw.width_mm),
    length_mm: numOrNull(raw.length_mm),
  };
}

function hydrateStage(raw: ProcessPlanStage): ProcessPlanStage {
  return {
    ...raw,
    inputs: (raw.inputs ?? []).map(hydrateInput),
    outputs: (raw.outputs ?? []).map(hydrateOutput),
  };
}

async function replaceStages(planId: string, values: ProcessPlanFormValues) {
  await supabase
    .from("process_plan_stages")
    .update({ previous_stage_id: null })
    .eq("process_plan_id", planId);

  const { error: delErr } = await supabase
    .from("process_plan_stages")
    .delete()
    .eq("process_plan_id", planId);
  if (delErr) throw delErr;

  const stageIds: string[] = [];

  for (let i = 0; i < values.stages.length; i++) {
    const stage = values.stages[i];
    const { data: inserted, error: stageErr } = await supabase
      .from("process_plan_stages")
      .insert({
        process_plan_id: planId,
        stage_index: i + 1,
        process_type_id: stage.process_type_id,
        machine_id: stage.machine_id,
        input_source: stage.input_source,
        previous_stage_id: null,
        status: "planned",
        planned_start_at: text(stage.planned_start_at),
        planned_end_at: text(stage.planned_end_at),
        quality_check_required: stage.quality_check_required,
        notes: text(stage.notes),
      })
      .select("id")
      .single();
    if (stageErr) throw stageErr;
    stageIds.push(inserted.id);

    if (stage.input_source === "inventory" && stage.inputs.length) {
      const { error: inErr } = await supabase.from("process_plan_inputs").insert(
        stage.inputs.map((inp, li) => ({
          stage_id: inserted.id,
          line_no: li + 1,
          inventory_batch_id: inp.inventory_batch_id,
          planned_qty: inp.planned_qty,
          planned_weight: inp.planned_weight,
        })),
      );
      if (inErr) throw inErr;
    }

    const { error: outErr } = await supabase.from("process_plan_outputs").insert(
      stage.outputs.map((out, li) => ({
        stage_id: inserted.id,
        line_no: li + 1,
        material_id: out.material_id,
        material_grade_id: text(out.material_grade_id),
        shape_size_id: text(out.shape_size_id),
        planned_qty: out.planned_qty,
        planned_weight: out.planned_weight,
        thickness_mm: numOrNull(out.thickness_mm),
        width_mm: numOrNull(out.width_mm),
        length_mm: numOrNull(out.length_mm),
        notes: text(out.notes),
      })),
    );
    if (outErr) throw outErr;
  }

  for (let i = 0; i < values.stages.length; i++) {
    if (values.stages[i].input_source !== "previous_stage" || i === 0) continue;
    const { error } = await supabase
      .from("process_plan_stages")
      .update({ previous_stage_id: stageIds[i - 1] })
      .eq("id", stageIds[i]);
    if (error) throw error;
  }
}

export function planToForm(plan: ProcessPlan): ProcessPlanFormValues {
  return {
    plan_date: plan.plan_date,
    sales_order_id: plan.sales_order_id ?? "",
    sales_order_line_id: plan.sales_order_line_id ?? "",
    notes: plan.notes ?? "",
    stages: (plan.stages ?? []).map((s) => ({
      process_type_id: s.process_type_id,
      machine_id: s.machine_id,
      input_source: s.input_source,
      planned_start_at: s.planned_start_at ?? "",
      planned_end_at: s.planned_end_at ?? "",
      quality_check_required: s.quality_check_required,
      notes: s.notes ?? "",
      inputs:
        s.inputs?.length ?
          s.inputs.map((inp) => ({
            inventory_batch_id: inp.inventory_batch_id,
            planned_qty: inp.planned_qty,
            planned_weight: inp.planned_weight,
          }))
        : [],
      outputs: (s.outputs ?? []).map((out) => ({
        material_id: out.material_id,
        material_grade_id: out.material_grade_id ?? "",
        shape_size_id: out.shape_size_id ?? "",
        planned_qty: out.planned_qty,
        planned_weight: out.planned_weight,
        thickness_mm: out.thickness_mm ?? undefined,
        width_mm: out.width_mm ?? undefined,
        length_mm: out.length_mm ?? undefined,
        notes: out.notes ?? "",
      })),
    })),
  };
}

export function useProcessPlans() {
  return useQuery({
    queryKey: ["process_plans"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ProcessPlanListRow[]> => {
      const { data, error } = await supabase
        .from("process_plans")
        .select(
          `*,
          sales_orders(so_number, clients(name)),
          process_plan_stages(id, stage_index, process_types(name))`,
        )
        .order("plan_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const so = raw.sales_orders as {
          so_number?: string;
          clients?: { name?: string } | null;
        } | null;
        const stages = (raw.process_plan_stages as Record<string, unknown>[] | null) ?? [];
        const { sales_orders: _s, process_plan_stages: _p, ...rest } = raw;
        const names = stages
          .slice()
          .sort((a, b) => money(a.stage_index) - money(b.stage_index))
          .map((s) => (s.process_types as { name?: string } | null)?.name)
          .filter(Boolean)
          .join(" → ");
        return {
          ...(rest as ProcessPlan),
          so_number: so?.so_number ?? null,
          client_name: so?.clients?.name ?? null,
          stage_count: stages.length,
          process_names: names || null,
        };
      });
    },
  });
}

export function useProcessPlan(id?: string) {
  return useQuery({
    queryKey: ["process_plans", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<ProcessPlan> => {
      const { data, error } = await supabase
        .from("process_plans")
        .select(
          `*,
          sales_orders(so_number, clients(name)),
          process_plan_stages(
            *,
            process_types(code, name),
            machines(code, name),
            process_plan_inputs(
              *,
              inventory_batches(batch_no, materials(name))
            ),
            process_plan_outputs(
              *,
              materials(name),
              material_grades(name),
              shape_sizes(size_label)
            )
          )`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const so = raw.sales_orders as {
        so_number?: string;
        clients?: { name?: string } | null;
      } | null;
      const stagesRaw = (raw.process_plan_stages as Record<string, unknown>[] | null) ?? [];
      const { sales_orders: _s, process_plan_stages: _p, ...rest } = raw;

      const stages = stagesRaw
        .map((s) => {
          const pt = s.process_types as { code?: string; name?: string } | null;
          const m = s.machines as { code?: string; name?: string } | null;
          const inputsRaw = (s.process_plan_inputs as Record<string, unknown>[] | null) ?? [];
          const outputsRaw = (s.process_plan_outputs as Record<string, unknown>[] | null) ?? [];
          const {
            process_types: _pt,
            machines: _m,
            process_plan_inputs: _i,
            process_plan_outputs: _o,
            ...stageRest
          } = s;
          return hydrateStage({
            ...(stageRest as ProcessPlanStage),
            process_name: pt?.name ?? null,
            process_code: pt?.code ?? null,
            machine_code: m?.code ?? null,
            machine_name: m?.name ?? null,
            inputs: inputsRaw
              .map((inp) => {
                const batch = inp.inventory_batches as {
                  batch_no?: string;
                  materials?: { name?: string } | null;
                } | null;
                const { inventory_batches: _b, ...inpRest } = inp;
                return hydrateInput({
                  ...(inpRest as ProcessPlanInput),
                  batch_no: batch?.batch_no ?? null,
                  material_name: batch?.materials?.name ?? null,
                });
              })
              .sort((a, b) => a.line_no - b.line_no),
            outputs: outputsRaw
              .map((out) => {
                const mat = out.materials as { name?: string } | null;
                const grade = out.material_grades as { name?: string } | null;
                const shape = out.shape_sizes as { size_label?: string } | null;
                const {
                  materials: _mat,
                  material_grades: _g,
                  shape_sizes: _sh,
                  ...outRest
                } = out;
                return hydrateOutput({
                  ...(outRest as ProcessPlanOutput),
                  material_name: mat?.name ?? null,
                  grade_name: grade?.name ?? null,
                  shape_label: shape?.size_label ?? null,
                });
              })
              .sort((a, b) => a.line_no - b.line_no),
          });
        })
        .sort((a, b) => a.stage_index - b.stage_index);

      return {
        ...(rest as ProcessPlan),
        so_number: so?.so_number ?? null,
        client_name: so?.clients?.name ?? null,
        stages,
      };
    },
  });
}

export function useCreateProcessPlan() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: ProcessPlanFormValues) => {
      const planNo = await nextPlanNumber(values.plan_date);
      const { data, error } = await supabase
        .from("process_plans")
        .insert({
          plan_no: planNo,
          plan_date: values.plan_date,
          sales_order_id: text(values.sales_order_id),
          sales_order_line_id: text(values.sales_order_line_id),
          status: "draft",
          notes: text(values.notes),
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;
      await replaceStages(data.id, values);
      return data as ProcessPlan;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["process_plans"] });
      toast.success("Plan created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create plan"),
  });
}

export function useUpdateProcessPlan() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: ProcessPlanFormValues }) => {
      const { data: existing, error: exErr } = await supabase
        .from("process_plans")
        .select("status")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      if (!["draft", "planned"].includes(existing.status as string)) {
        throw new Error("Only draft or planned plans can be edited");
      }

      const { data, error } = await supabase
        .from("process_plans")
        .update({
          plan_date: values.plan_date,
          sales_order_id: text(values.sales_order_id),
          sales_order_line_id: text(values.sales_order_line_id),
          notes: text(values.notes),
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      await replaceStages(id, values);
      return data as ProcessPlan;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["process_plans"] });
      await queryClient.invalidateQueries({ queryKey: ["process_plans", vars.id] });
      toast.success("Plan saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save plan"),
  });
}

export function useUpdateProcessPlanStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ProcessPlanStatus }) => {
      const { data, error } = await supabase
        .from("process_plans")
        .update({
          status,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as ProcessPlan;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["process_plans"] });
      await queryClient.invalidateQueries({ queryKey: ["process_plans", vars.id] });
      toast.success(`Plan marked ${vars.status.replace("_", " ")}`);
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function useDeleteProcessPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: existing, error: exErr } = await supabase
        .from("process_plans")
        .select("status")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      if (existing.status !== "draft") throw new Error("Only draft plans can be deleted");
      const { error } = await supabase.from("process_plans").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["process_plans"] });
      toast.success("Plan deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete plan"),
  });
}
